import { cloneDeep } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLocalStorage } from '../../hooks/localstorage';
import {postMember, postPromoCode, postValidateOrder, postValidateTimeslot} from '../../services/api/basket';
import {Item, ModifierShort, Timeslot, TSite} from '../site/site-context';
import useSiteContext from '../site/useSiteContext';
import { BasketContext, BasketContextData, Order, OrderBasket, Price, TBasket } from './basket-context';
import {useLocation} from "react-router-dom";
import {iLog} from "../../index";
import useGroupContext from '../group/useGroupContext';
import { fromGroupLine, lineChanges } from '../../services/groupOrder';
import { toastEmitter } from '../../services/toast';
import { t } from 'ttag';
import { toItems, track, valueOf } from '../../services/analytics';

const BasketContextProvider = ({ children }: { children: JSX.Element }) => {
    const [basket, setBasket] = useState<TBasket | null | undefined>(undefined);
    const [basketItems, setBasketItems] = useState<Item[] | null>(null);

    const [tableNumber, setTableNumber] = useState<string | null>(null);
    const [customerEmail, setCustomerEmail] = useState<string | null>(null);
    const [specialNotes, setSpecialNotes] = useState<string | null | undefined>(undefined);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [promoError, setPromoError] = useState('');

    const [timeslot, setTimeslot] = useState<Timeslot | null>(null);

    const [validatedBasket, setValidatedBasket] = useState<TBasket | null | undefined>(undefined);
    const [validatedSite, setValidatedSite] = useState<TSite | null | undefined>(undefined);

    const [price, setPrice] = useState<Price>({
        deliveryFee: 0,
        serviceFee: 0,
        subtotal: 0,
        total: 0,
    });

    const siteContext = useSiteContext();
    const groupContext = useGroupContext();

    const [storedBasket, setStoredBasket] = useLocalStorage('basket', null);

    const navigate = useNavigate();

    const location = useLocation();

    // Clear basket if on the homepage.
    useEffect(() => {
        if (location.pathname === '/') {
            clearBasket();
        }
    }, []);


    // Load basket from storage if same site.
    useEffect(() => {
        if ((storedBasket || basketItems === null) && siteContext.site) {
            if (siteContext.site?.uid === storedBasket?.siteId) {
                setBasketItems(storedBasket?.basketItems ?? []);
                setTimeslot(storedBasket.timeslot);
            } else {
                setBasketItems([]);
            }
        }
    }, [siteContext.site]);

    // attempt to set local basket to saved to allow checkout reload
    useEffect(() => {
        if (storedBasket !== undefined && basket === undefined) {
            setBasket(storedBasket?.validated_basket ?? null);
            setValidatedBasket(storedBasket?.validated_basket ?? null);
            setValidatedSite(storedBasket?.validated_basket_site ?? null);
            setTableNumber(storedBasket?.validated_table_number ?? null);
        }
    }, [storedBasket]);

    // Sync local basket to storage.
    useEffect(() => {

        // Checks basketItems have actually loaded in before syncing to storedBasket.
        if (basketItems !== null || timeslot !== null) {
            iLog('Basket loaded, syncing stored basket items and timeslot.', [basketItems, timeslot]);
            setStoredBasket({
                ...(storedBasket ?? {}),
                basketItems,
                siteId: siteContext.site?.uid ?? '',
                timeslot: timeslot
            });
        }
    }, [basketItems, timeslot]);

    // In a group order the basket is the group order's: what the lead and the guests have added.
    useEffect(() => {
        if (!groupContext.active || !siteContext.menu) return;

        setBasketItems(groupContext.lines.map((line) => fromGroupLine(line, siteContext.menu)));
    }, [groupContext.active, groupContext.revision, siteContext.menu]);

    // Changes to the basket go to the group order as well, unless the lead is checking out and it's a guest's.
    // (`before` is what it was, for when adding to a line changes it where it stands)
    const commitItems = (next: Item[], before: Item[] = basketItems ?? []): boolean => {
        if (groupContext.active) {
            // (the lead can still change things while choosing a time: it's the guests who can't)
            if (groupContext.locked && !groupContext.isLead) {
                toastEmitter.showToast({
                    message: t`${groupContext.host ?? 'The host'} is checking out, so items can't be changed right now.`,
                    type: 'danger',
                });
                return false;
            }

            groupContext.sendChanges(lineChanges(before, next));
        }

        setBasketItems(next);

        return true;
    };

    // Calculate totals
    useEffect(() => {
        let [subtotal, deliveryFee, serviceFee, total] = [0, 0, 0, 0];

        basketItems?.forEach((item) => {
            subtotal += (item.modifiersTotal ?? 0) * (item.quantity ?? 1); //(item.modifiers?.reduce((total, mod) => total + (mod.price * (mod.quantity ?? 0)), 0) ?? 0);
            subtotal += item.price * (item.quantity ?? 1);
        });

        iLog('basket items totals calc', [subtotal, deliveryFee, serviceFee, total]);

        total = subtotal + deliveryFee + serviceFee;
        setPrice({
            deliveryFee,
            serviceFee,
            subtotal,
            total,
        });

        iLog('basket items totals calc', basketItems);

    }, [basketItems]);

    // The flow ID is meant to stay constant for the whole pre-order session.
    // basket.flow is persisted (via storedBasket) and survives a page reload,
    // whereas siteContext.flow is only ever populated by site/menu/timeslot
    // fetches and resets on a reload or clearBasket(). Prefer basket.flow so
    // requests made after a reload still carry the real flow instead of
    // silently falling back to the 'noflow' sentinel.
    const getCurrentFlow = (): string => {
        return basket?.flow || siteContext.flow || 'noflow';
    };

    const validateBasket = async (removePromo: boolean = false, navigateToCheckout: boolean = true) => {
        if (!siteContext.site?.uid) {
            console.error('No valid site set on basket.');
            return;
        }

        setLoading(true);

        // clear previously validated info
        setStoredBasket((currentBasket: TBasket | null | undefined) => ({
            ...currentBasket,
            validated_basket: null,
            validated_basket_site: null,
            validated_table_number: null,
        }));

        let orderBasket: OrderBasket[] =
            basketItems?.map((item) => {
                return {
                    item_id: item.uid,
                    quantity: item.quantity ?? 1,
                    modifiers:
                        item.modifiers?.map((a) => {
                            // if (a.quantity === -1) {
                            //     return { modifier_id: '-' + a.uid };
                            // } else {
                                return { modifier_id: a.uid, quantity: a.quantity ?? 1 };
                            //}
                        }) ?? [],
                };
            }) ?? [];

        let order: Order = {
            uid: siteContext.site!.uid,
            channel: siteContext.channel,
            time: timeslot?.id,
            asap: timeslot && timeslot.is_asap ? true : false,
            basket: JSON.stringify(orderBasket),
            subtotal: price.total,
            flow: getCurrentFlow(),
            special_notes: specialNotes,
            remove_promo: removePromo,
            address: localStorage.getItem('address'),
            delivery_venue: localStorage.getItem('delivery-venue') ?? '',
            // the lead checks out with everyone's items, which the API has
            ...(groupContext.active && groupContext.isLead && groupContext.session ? {
                group_token: groupContext.session.token,
                group_participant_token: groupContext.session.participantToken,
            } : {}),
        };

        iLog('order', order);
        await postValidateOrder(order)
            .then((data: any) => {
                iLog('Validating...', [data.basket_id, data]);
                updateValidatedBasket(data);
                setPromoError('');

                if (navigateToCheckout) {
                    navigate('/checkout');
                }
                // TODO: SAVE this data, required for the confirmation page - ALSO USE BASKET RETURNED HERE FOR BASKET on confirmation
            })
            .finally(() => setLoading(false));
    };

    const validateTimeslot = async () => {
        if (!siteContext.site?.uid) {
            console.error('No valid site set on basket.');
            return;
        }

        setLoading(true);

        await postValidateTimeslot(
                validatedBasket?.basket_id,
                timeslot?.id,
                !!(timeslot && timeslot.is_asap),
                getCurrentFlow(),
            )
            .then((data: any) => {
                iLog('Validating timeslot...', [data.basket_id, data]);
                setBasket((prev: any) => data);
                setStoredBasket((currentBasket: TBasket | null | undefined) => ({
                    ...currentBasket,
                    validated_basket: { ...storedBasket?.validated_basket,  ready_time: data.ready_time },
                }));

            })
            .finally(() => setLoading(false));
    };

    const updateValidatedBasket = (data: any) => {
        if (!data.basket_id) {
            throw new Error('No basket id retrieved on basket validation.');
        }

        // removed data merging as it was not triggering useEffect properly in ordercontext

        iLog('updateValidatedBasket', [data, validatedBasket]);

        let ready_time = data.ready_time;

        let new_basket = { ...data, channel: siteContext.channel };

        if ( ready_time ) {
            new_basket = { ...data, channel: siteContext.channel, ready_time: ready_time }
        }

        setBasket((prev: any) => data);
        setStoredBasket((currentBasket: TBasket | null | undefined) => ({
            ...currentBasket,
            validated_basket: new_basket,
            validated_basket_site: siteContext.site,
            validated_table_number: tableNumber,
        }));

        if (data.timeslots) {
            siteContext.setTimeslots(data.timeslots);
        }
    };

    useEffect(() => {
        setValidatedBasket(storedBasket?.validated_basket ?? null);
        setValidatedSite(storedBasket?.validated_basket_site ?? null);
    }, [storedBasket]);

    const addItem = (item: Item, quantity: number, edit: boolean) => {
        iLog('add item', item);

        if ( quantity < 1 ) {
            iLog('BasketContextProvider.addItem: quantity is 0', null);
            return removeItem(item);
        }

        let i = cloneDeep(item);

        iLog('cloned deep', i);

        // extract selected modifiers from their groups
        let foo: ModifierShort[] = [];
        i.modifier_groups.forEach((group) => {
            return group.modifiers.forEach((mod) => {
                if (mod.quantity > 0) {
                    foo.push({
                        uid: mod.uid,
                        name: mod.name,
                        quantity: mod.quantity,
                        group_uid: mod.group_uid ?? ''
                    });
                }

                // unselecting default mod
                if ( mod.is_default && mod.quantity === 0 ) {
                    foo.push({
                        uid: mod.uid,
                        name: mod.name,
                        quantity: -1,
                        group_uid: mod.group_uid ?? ''
                    });
                }
            });
        })
        i.modifiers = foo;

        if ( !i.basketIndex ) {
            let str = makeid(groupContext.active ? 12 : 5);
            iLog('no index, making one..', str)
            i.basketIndex = str;
        }

        iLog('add or edit?', [edit, i]);

        // what the basket was before this changes it
        const before = cloneDeep(basketItems ?? []);

        if ( groupContext.active && !i.groupLine ) {
            i.groupLine = { participant: groupContext.name, mine: true, problem: null };
        }

        // by how much the line grows (or shrinks, when it is edited to fewer)
        const change = edit
            ? quantity - (before.find((line) => line.basketIndex === i.basketIndex)?.quantity ?? 0)
            : quantity;

        const done = edit
            ? commitItems(editItemInBasketItems(basketItems ?? [], i, quantity), before)
            : commitItems(addItemToBasketItems(basketItems ?? [], i, quantity), before);

        if (done && change !== 0) {
            trackBasketChange(change > 0 ? 'add_to_cart' : 'remove_from_cart', i, Math.abs(change));
        }
    };

    const trackBasketChange = (name: 'add_to_cart' | 'remove_from_cart', item: Item, quantity: number) =>
        track(name, { channel: siteContext.channel, site_handle: siteContext.site?.handle }, {
            ecommerce: { value: valueOf([item], quantity), items: toItems([item], quantity) },
        });

    function makeid(length: number) {
        var result           = '';
        var characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        var charactersLength = characters.length;
        for ( var i = 0; i < length; i++ ) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    const editItemInBasketItems = (currentItems: Item[], newItem: Item, newItemQuantity: number = 1) => {
        let newItems = [...currentItems];

        iLog('edit in basket', [newItem.basketIndex, newItems]);

        if ( newItem.basketIndex ) {

            newItems.forEach(function(item, i) {
                if (item.basketIndex === newItem.basketIndex) {
                    newItems[i] = newItem;
                    newItems[i].quantity = newItemQuantity;
                }
            });


            iLog('editing items', newItems);
        }

        return newItems;
    };

    const addItemToBasketItems = (
        currentItems: Item[],
        newItem: Item,
        newItemQuantity: number = 1,
    ): Item[] => {
        let exactMatchAdded = false;
        let newBasketItems = [...currentItems];

        iLog('add item in basket', newItem);

        newBasketItems = currentItems.map((cItem: Item) => {
            if (exactMatchAdded) {
                return cItem;
            }

            if (cItem.uid !== newItem.uid) {
                return cItem;
            }

            // what somebody else added stays theirs
            if (cItem.groupLine && !cItem.groupLine.mine) {
                return cItem;
            }

            // Not possible for mods to match
            if (cItem.modifiers?.length !== newItem.modifiers?.length) {
                return cItem;
            }

            // every modifier in this item matches the new item
            let exactMatchFound = cItem.modifiers?.every((cMod) => {
                return newItem.modifiers?.some((newMod) => {
                    if (cMod.uid === newMod.uid && cMod.quantity === newMod.quantity) {
                        return true;
                    }
                });
            });

            if (exactMatchFound) {
                cItem.quantity = (cItem.quantity ?? 1) + newItemQuantity;
                exactMatchAdded = true;
            }

            return cItem;
        });

        if (!exactMatchAdded) {
            newItem.quantity = (newItem.quantity ?? 0) + newItemQuantity;
            return [...newBasketItems, newItem];
        } else {
            return newBasketItems;
        }
    };

    const hasExactItemMatch = (currentItems: Item[], newItem: Item): boolean => {
        if (!currentItems.length) {
            return false;
        }

        return currentItems.some((cItem: Item) => {
            if (cItem.uid !== newItem.uid) {
                return false;
            }

            // Not possible for mods to match
            if (cItem.modifiers?.length !== newItem.modifiers?.length) {
                return false;
            }

            // every modifier in this item matches the new item
            return cItem.modifiers?.every((cMod) => {
                return newItem.modifiers?.some((newMod) => {
                    if (cMod.uid === newMod.uid) {
                        //&& cMod.quantity === newMod.quantity
                        return true;
                    }
                });
            });
        });
    };

    const removeItem = (item: Item) => {
        iLog(`Removing item from basket.`, item);

        const line = (basketItems ?? []).find((basketItem) =>
            item.basketIndex ? basketItem.basketIndex === item.basketIndex : basketItem.uid === item.uid
        );
        trackBasketChange('remove_from_cart', line ?? item, line?.quantity ?? item.quantity ?? 1);

        // in a group order lines are told apart by their own id, as the same item can be in the basket several times
        if ( groupContext.active && item.basketIndex ) {
            commitItems((basketItems ?? []).filter((basketItem) => basketItem.basketIndex !== item.basketIndex));
            return;
        }

        let newBasket = basketItems?.filter((basketItem) => {
            let foundExactSameItem = false;

            if (basketItem.uid === item.uid && basketItem.modifiers?.length === item.modifiers?.length) {
                /* same length of modifiers - so now check if modifiers are the same  */
                let sameModifierCount = 0;
                basketItem.modifiers?.forEach((basketItemMod) => {
                    item.modifiers?.forEach((itemMod) => {
                        if (itemMod.uid === basketItemMod.uid && itemMod.quantity === basketItemMod.quantity) {
                            sameModifierCount++;
                        }
                    });
                });

                if (sameModifierCount === item.modifiers?.length) {
                    return false;
                }
            } else if (basketItem.uid === item.uid) {
                // else just remove any with same id anyway
            }

            return true;
        });

        setBasketItems(newBasket ?? []);
    };

    const clearBasket = () => {
        setBasketItems([]);
        setValidatedBasket(null);
        setValidatedSite(null);
        setTimeslot(null);
        siteContext.setFlow('noflow');
        siteContext.setTimeslots([]);
        setStoredBasket(null);
    };

    // What was in the basket was the group order's, so it goes when the group order does (left, ended or done with)
    const hadGroupSession = useRef(!!groupContext.session);

    useEffect(() => {
        if (hadGroupSession.current && !groupContext.session) {
            clearBasket();
        }

        hadGroupSession.current = !!groupContext.session;
    }, [groupContext.session]);

    const validatePromoCode = async (code: string, email: string | null) => {

        setPromoError('');

        await postPromoCode(
            validatedBasket?.basket_id ?? '',
            code,
            validatedBasket?.payment_intent?.client_secret ?? '',
            getCurrentFlow(),
            email,
        ).then((data) => {

            // add the previously stored ready_time
            data.ready_time = validatedBasket?.ready_time;

            iLog('validatePromoCode', data);

            updateValidatedBasket(data);

        }).catch(err => {

            iLog('validatePromoCode err', err);
            setPromoError(err.message);
        });
    };

    const validateMemberId = async (email: string, member: string) => {

        await postMember(
            validatedBasket?.basket_id ?? '',
            email,
            member,
            getCurrentFlow()
        )
            .then((data) => {
                iLog('validatePromoCode', data);
                return data;
            }).catch(err => {
                iLog('validatePromoCode err', err);
                throw err;
            });
    };

    let basketContextValue: BasketContextData = {
        basket,
        setBasket,

        basketItems: basketItems ?? [],
        setBasketItems,

        loading,
        setLoading,

        validateBasket,
        validateTimeslot,
        clearBasket,

        validatePromoCode,
        validateMemberId,

        validatedBasket,
        validatedSite,

        addItem,
        removeItem,

        timeslot,
        setTimeslot,

        price,
        setPrice,
        tableNumber,
        setTableNumber,

        customerEmail,
        setCustomerEmail,

        error,
        setError,

        promoError,
        setPromoError,

        specialNotes,
        setSpecialNotes

    };
    return <BasketContext.Provider value={basketContextValue}>{children}</BasketContext.Provider>;
};

export default BasketContextProvider;
